import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import Sidebar from './components/common/Sidebar';
import Navbar from './components/common/Navbar';
import RightSidebar from './components/common/RightSidebar';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import MyTasks from './pages/MyTasks';
import CompletedTasks from './pages/CompletedTasks';
import PendingDoneRequests from './pages/PendingDoneRequests';
import TaskDetail from './pages/TaskDetail';
import Enrollments from './pages/Enrollments';
import ScheduledTasks from './pages/ScheduledTasks';
import ScheduleCreate from './pages/ScheduleCreate';
import ScheduleList from './pages/ScheduleList';
import ScheduleDetail from './pages/ScheduleDetail';
import ScheduleEdit from './pages/ScheduleEdit';
import AdminStaff from './pages/AdminStaff';
import AdminStaffCreate from './pages/AdminStaffCreate';
import AdminTasks from './pages/AdminTasks';
import AdminEnrollments from './pages/AdminEnrollments';
import Organizations from './pages/Organizations';
import StaffPerformanceReport from './pages/StaffPerformanceReport';
import StaffWorkloadReport from './pages/StaffWorkloadReport';
import TaskPriorityReport from './pages/TaskPriorityReport';
import TaskActivityLogReport from './pages/TaskActivityLogReport';
import OverdueTasksReport from './pages/OverdueTasksReport';
import NotDoneTasksReport from './pages/NotDoneTasksReport';
import Profile from './pages/Profile';
import UserProfile from './pages/UserProfile';
import Notepad from './pages/Notepad';
import AdminNotepad from './pages/AdminNotepad';
import DatabaseBackup from './pages/DatabaseBackup';
import PublicTaskSubmit from './pages/PublicTaskSubmit';
import AssessmentsAssigned from './pages/AssessmentsAssigned';
import AssessmentsCreated from './pages/AssessmentsCreated';
import AssessmentCreate from './pages/AssessmentCreate';
import AssessmentTake from './pages/AssessmentTake';
import AssessmentReview from './pages/AssessmentReview';
import AssessmentDetail from './pages/AssessmentDetail';
import AssessmentCompletionReport from './pages/AssessmentCompletionReport';

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="flex-center loading" style={{ minHeight: '100vh' }}>
      <div className="spinner" />
      <span className="loading-text">Loading...</span>
    </div>
  );
  return user ? children : <Navigate to="/login" />;
};

const AdminRoute = ({ children }) => {
  const { user, isAdmin } = useAuth();
  if (!user) return <Navigate to="/login" />;
  if (!isAdmin) return <Navigate to="/dashboard" />;
  return children;
};

/** Reports: only Super Admin and Admin; Accounts and HR are redirected */
const AdminReportsRoute = ({ children }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" />;
  const role = user?.role === 'user' ? 'staff' : user?.role;
  const reportsAllowed = role === 'super_admin' || role === 'hr' || role === 'accounts';
  if (!reportsAllowed) return <Navigate to="/dashboard" />;
  return children;
};

/** Organization/Staff management: hidden from Accounts; Super Admin, Admin, HR only */
const OrgManagementRoute = ({ children }) => {
  const { user, isAdmin } = useAuth();
  if (!user) return <Navigate to="/login" />;
  if (!isAdmin) return <Navigate to="/dashboard" />;
  const role = user?.role === 'user' ? 'staff' : user?.role;
  if (role === 'accounts') return <Navigate to="/dashboard" />;
  return children;
};

const SuperAdminRoute = ({ children }) => {
  const { user, isSuperAdmin } = useAuth();
  if (!user) return <Navigate to="/login" />;
  if (!isSuperAdmin) return <Navigate to="/dashboard" />;
  return children;
};

/** /admin default: staff for non-Accounts, enrollments for Accounts */
const AdminDefaultRedirect = () => {
  const { user, isAdmin } = useAuth();
  if (!user) return <Navigate to="/login" />;
  if (!isAdmin) return <Navigate to="/dashboard" />;
  const role = user?.role === 'user' ? 'staff' : user?.role;
  if (role === 'accounts') return <Navigate to="/admin/enrollments" replace />;
  return <Navigate to="/admin/staff" replace />;
};

const AppLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  useEffect(() => {
    if (sidebarOpen) {
      document.body.classList.add('sidebar-drawer-open');
    } else {
      document.body.classList.remove('sidebar-drawer-open');
    }
    return () => document.body.classList.remove('sidebar-drawer-open');
  }, [sidebarOpen]);
  return (
    <div className={`app-layout ${sidebarOpen ? 'sidebar-drawer-open' : ''}`}>
      {sidebarOpen && (
        <div
          className="sidebar-overlay"
          aria-hidden="false"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
      <div className="main-content">
        <Navbar onMenuClick={() => setSidebarOpen((o) => !o)} />
        {children}
      </div>
      <RightSidebar />
    </div>
  );
};

const AppRoutes = () => {
  const { user } = useAuth();
  return (
    <Routes>
      {/* Public routes — no authentication required */}
      <Route path="/submit" element={<PublicTaskSubmit />} />
      <Route path="/public/assessments/:token" element={<AssessmentTake />} />

      <Route path="/login"    element={user ? <Navigate to="/dashboard" /> : <Login />} />
      <Route path="/register" element={user ? <Navigate to="/dashboard" /> : <Register />} />
      <Route path="/forgot-password" element={user ? <Navigate to="/dashboard" /> : <ForgotPassword />} />
      <Route path="/reset-password" element={user ? <Navigate to="/dashboard" /> : <ResetPassword />} />

      <Route path="/dashboard"   element={<PrivateRoute><AppLayout><Dashboard /></AppLayout></PrivateRoute>} />
      <Route path="/tasks"       element={<PrivateRoute><AppLayout><MyTasks /></AppLayout></PrivateRoute>} />
      <Route path="/completed-tasks" element={<PrivateRoute><AppLayout><CompletedTasks /></AppLayout></PrivateRoute>} />
      <Route path="/pending-done-requests" element={<PrivateRoute><AppLayout><PendingDoneRequests /></AppLayout></PrivateRoute>} />
      {/* Backwards compatibility: keep /scheduled-tasks showing create+list */}
      <Route path="/scheduled-tasks" element={<PrivateRoute><AppLayout><ScheduledTasks /></AppLayout></PrivateRoute>} />
      <Route path="/scheduled-tasks/new" element={<PrivateRoute><AppLayout><ScheduleCreate /></AppLayout></PrivateRoute>} />
      <Route path="/scheduled-tasks/all" element={<PrivateRoute><AppLayout><ScheduleList /></AppLayout></PrivateRoute>} />
      <Route path="/scheduled-tasks/:id" element={<PrivateRoute><AppLayout><ScheduleDetail /></AppLayout></PrivateRoute>} />
      <Route path="/scheduled-tasks/:id/edit" element={<PrivateRoute><AppLayout><ScheduleEdit /></AppLayout></PrivateRoute>} />
      <Route path="/tasks/:id" element={<PrivateRoute><AppLayout><TaskDetail /></AppLayout></PrivateRoute>} />
      <Route path="/enrollments" element={<PrivateRoute><AppLayout><Enrollments /></AppLayout></PrivateRoute>} />
      <Route path="/profile"     element={<PrivateRoute><AppLayout><Profile /></AppLayout></PrivateRoute>} />
      <Route path="/users/:id" element={<PrivateRoute><AppLayout><UserProfile /></AppLayout></PrivateRoute>} />
      <Route path="/notepad"   element={<PrivateRoute><AppLayout><Notepad /></AppLayout></PrivateRoute>} />

      <Route path="/assessments" element={<PrivateRoute><AppLayout><AssessmentsAssigned /></AppLayout></PrivateRoute>} />
      <Route path="/assessments/created" element={<PrivateRoute><AppLayout><AssessmentsCreated /></AppLayout></PrivateRoute>} />
      <Route path="/assessments/new" element={<PrivateRoute><AppLayout><AssessmentCreate /></AppLayout></PrivateRoute>} />
      <Route path="/assessments/reports/completion" element={<PrivateRoute><AppLayout><AssessmentCompletionReport /></AppLayout></PrivateRoute>} />
      <Route path="/assessments/:id/edit" element={<PrivateRoute><AppLayout><AssessmentCreate /></AppLayout></PrivateRoute>} />
      <Route path="/assessments/:id/view" element={<PrivateRoute><AppLayout><AssessmentDetail /></AppLayout></PrivateRoute>} />
      <Route path="/assessments/:id/take" element={<PrivateRoute><AppLayout><AssessmentTake /></AppLayout></PrivateRoute>} />
      <Route path="/assessments/:id/review/:attemptId" element={<PrivateRoute><AppLayout><AssessmentReview /></AppLayout></PrivateRoute>} />
      <Route path="/assessments/:id/review" element={<PrivateRoute><AppLayout><AssessmentReview /></AppLayout></PrivateRoute>} />

      <Route path="/admin/staff"         element={<OrgManagementRoute><AppLayout><AdminStaff /></AppLayout></OrgManagementRoute>} />
      <Route path="/admin/staff/new"     element={<OrgManagementRoute><AppLayout><AdminStaffCreate /></AppLayout></OrgManagementRoute>} />
      <Route path="/admin/staff/:id/edit" element={<OrgManagementRoute><AppLayout><AdminStaffCreate /></AppLayout></OrgManagementRoute>} />
      <Route path="/admin/tasks"         element={<AdminRoute><AppLayout><AdminTasks /></AppLayout></AdminRoute>} />
      <Route path="/admin/organizations" element={<OrgManagementRoute><AppLayout><Organizations /></AppLayout></OrgManagementRoute>} />
      <Route path="/admin/enrollments"          element={<AdminRoute><AppLayout><AdminEnrollments /></AppLayout></AdminRoute>} />
      <Route path="/admin/reports/performance"  element={<AdminReportsRoute><AppLayout><StaffPerformanceReport /></AppLayout></AdminReportsRoute>} />
      <Route path="/admin/reports/workload"     element={<AdminReportsRoute><AppLayout><StaffWorkloadReport /></AppLayout></AdminReportsRoute>} />
      <Route path="/admin/reports/priority"     element={<AdminReportsRoute><AppLayout><TaskPriorityReport /></AppLayout></AdminReportsRoute>} />
      <Route path="/admin/reports/activity"     element={<AdminReportsRoute><AppLayout><TaskActivityLogReport /></AppLayout></AdminReportsRoute>} />
      <Route path="/admin/reports/overdue"      element={<AdminReportsRoute><AppLayout><OverdueTasksReport /></AppLayout></AdminReportsRoute>} />
      <Route path="/admin/reports/not-done"     element={<AdminReportsRoute><AppLayout><NotDoneTasksReport /></AppLayout></AdminReportsRoute>} />
      <Route path="/admin/notepad"             element={<SuperAdminRoute><AppLayout><AdminNotepad /></AppLayout></SuperAdminRoute>} />
      <Route path="/admin/database-backup"     element={<SuperAdminRoute><AppLayout><DatabaseBackup /></AppLayout></SuperAdminRoute>} />

      <Route path="/"      element={<Navigate to="/dashboard" />} />
      <Route path="/admin" element={<AdminDefaultRedirect />} />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppRoutes />
        <Toaster
          position="top-right"
          containerStyle={{ marginTop: '80px' }}
          toastOptions={{ duration: 3000, style: { borderRadius: '10px', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 500 } }}
        />
      </Router>
    </AuthProvider>
  );
}

export default App;
